// react
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { clientTools, createChatClientOptions } from '@tanstack/ai-client';
import { fetchServerSentEvents, useChat } from '@tanstack/ai-react';

// material ui icons
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';

// material ui
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { logChatFeedback } from '@/utils/logChatFeedback';

// relative
import { getCurrentAccountDef } from '../tools/clientToolDefinitions';
import { AssistantMessage } from './AssistantMessage';
import { ChatFeedback } from './ChatFeedback';
import { ProviderSelector } from './ProviderSelector';
import { UserMessage } from './UserMessage';

import '@fortawesome/fontawesome-free/css/all.min.css';

// AI Provider types
type AIProvider = 'ollama' | 'anthropic' | 'openai' | 'azure';

// Interface for parent to call child methods
export interface ChatInterfaceHandle {
  clearMessages: () => void;
  hasMessages: () => boolean;
  sendMessage: (message: string) => void;
  isLoading: boolean;
  stop: () => void;
}

// Separate component to force remount on provider change
const ChatInterface = forwardRef<
  ChatInterfaceHandle,
  { provider: AIProvider; onLoadingChange: (loading: boolean) => void }
>(({ provider, onLoadingChange }, ref) => {
  const { t } = useTranslation('ai-assistant');
  const selectedAccount = useSelector(getSelectedAccount);
  const { session } = useSessionAuth();
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [lastFeedbackMessageCount, setLastFeedbackMessageCount] = useState(0);

  // Use ref to store latest selectedAccount value - fixes stale closure issue
  const selectedAccountRef = useRef(selectedAccount);

  // Track previous account to detect changes
  const prevAccountIdRef = useRef(selectedAccount?.AccountId);

  // Update ref whenever selectedAccount changes
  useEffect(() => {
    selectedAccountRef.current = selectedAccount;
    console.log('Selected account from Redux:', selectedAccount);
  }, [selectedAccount]);

  // Client tool implementation - executes in browser with Redux access
  // Tool function reads from ref to always get latest value
  // eslint-disable-next-line react-hooks/refs
  const getCurrentAccount = getCurrentAccountDef.client(() => {
    const accountId = selectedAccountRef.current?.AccountId;
    const accountName = selectedAccountRef.current?.Name;

    // console.log('[Client Tool] get_current_account called', {
    //   accountId,
    //   accountName,
    // });

    if (!accountId) {
      return {
        hasAccount: false,
        accountId: '',
        accountName: 'No account selected',
      };
    }

    return {
      hasAccount: true,
      accountId,
      accountName: accountName || 'Unknown',
    };
  });

  // Use helper functions from TanStack AI docs
  const tools = clientTools(getCurrentAccount);

  // Configure connection and model based on selected provider
  const connectionUrl =
    provider === 'ollama'
      ? '/api/ollama/chat'
      : provider === 'anthropic'
        ? '/api/anthropic/chat'
        : provider === 'azure'
          ? '/api/azure-openai/chat'
          : '/api/openai/chat';

  const modelName =
    provider === 'ollama'
      ? 'qwen3-coder:30b'
      : provider === 'anthropic'
        ? 'claude-4-5-sonnet-20250929'
        : provider === 'azure'
          ? 'gpt-4o'
          : 'gpt-4o';

  const chatOptions = createChatClientOptions({
    connection: fetchServerSentEvents(connectionUrl),
    body: {
      model: modelName,
    },
    tools,
  });

  const {
    messages,
    sendMessage,
    isLoading,
    error,
    setMessages,
    stop,
    addToolApprovalResponse,
  } = useChat(chatOptions);

  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Handler for feedback submission
  const handleChatFeedback = (
    feedback: 'positive' | 'negative',
    comments?: string,
  ) => {
    const userEmail = session?.claims?.email || 'unknown';
    const accountId = selectedAccount?.AccountId?.toString() || 'no-account';
    const accountName = selectedAccount?.Name || 'Unknown Account';

    logChatFeedback({
      userEmail,
      accountId,
      accountName,
      messages,
      feedback,
      provider,
      model: modelName,
      sessionId,
      comments,
    });

    // Mark feedback as given for current message count
    setFeedbackGiven(true);
    setLastFeedbackMessageCount(messages.length);
  };

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  // Reset feedback state when new messages arrive
  useEffect(() => {
    if (feedbackGiven && messages.length > lastFeedbackMessageCount) {
      setFeedbackGiven(false);
    }
  }, [messages.length, feedbackGiven, lastFeedbackMessageCount]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    clearMessages: () => {
      setMessages([]);
      setFeedbackGiven(false);
      setLastFeedbackMessageCount(0);
    },
    hasMessages: () => messages.length > 0,
    sendMessage,
    isLoading,
    stop,
  }));

  // Detect account changes and notify the LLM to invalidate its cached knowledge
  useEffect(() => {
    const currentAccountId = selectedAccount?.AccountId;
    const previousAccountId = prevAccountIdRef.current;

    // Initialize on first render
    if (prevAccountIdRef.current === undefined) {
      prevAccountIdRef.current = currentAccountId;
      return;
    }

    // If account changed and there are messages, inject a system notice
    if (currentAccountId !== previousAccountId && messages.length > 0) {
      console.log(
        '[ChatComponent] Account changed, injecting context update message',
      );

      const contextUpdateMessage = {
        id: `context-update-${Date.now()}`,
        role: 'assistant' as const,
        parts: [
          {
            type: 'text' as const,
            content: `[${t('messages.systemNotice')}: ${t('messages.accountSwitched')}]`,
          },
        ],
      };

      setMessages([...messages, contextUpdateMessage]);
      prevAccountIdRef.current = currentAccountId;
    }
  }, [selectedAccount, messages, setMessages, t]);

  // Debug: Log messages to see what's coming through
  // React.useEffect(() => {
  //   messages.forEach((msg, idx) => {
  //     console.log(`[ChatComponent] Message ${idx}:`, {
  //       role: msg.role,
  //       parts: msg.parts.map((p) => ({
  //         type: p.type,
  //         content: p.type === 'text' ? p.content?.substring(0, 50) : p,
  //       })),
  //     });
  //   });
  // }, [messages]);

  // Auto-scroll to bottom when messages update or while loading (streaming)
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [messages, isLoading]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.divider,
            borderRadius: '3px',
          },
        }}
      >
        {messages.length === 0 ? (
          <AssistantMessage
            parts={[
              {
                type: 'text',
                content: t('messages.welcome'),
              },
            ]}
            onApprove={() => {}}
            onDeny={() => {}}
          />
        ) : (
          messages.map((message) => {
            if (message.role === 'user') {
              return (
                <UserMessage key={message.id} parts={message.parts as never} />
              );
            }

            return (
              <AssistantMessage
                key={message.id}
                parts={message.parts as never}
                onApprove={(id) =>
                  addToolApprovalResponse({ id, approved: true })
                }
                onDeny={(id) =>
                  addToolApprovalResponse({ id, approved: false })
                }
              />
            );
          })
        )}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
              px: 2,
            }}
          >
            <Box
              sx={{
                maxWidth: '85%',
                p: 2,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  '& .dot': {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'typing 1.4s infinite',
                  },
                  '& .dot:nth-of-type(1)': {
                    animationDelay: '0s',
                  },
                  '& .dot:nth-of-type(2)': {
                    animationDelay: '0.2s',
                  },
                  '& .dot:nth-of-type(3)': {
                    animationDelay: '0.4s',
                  },
                  '@keyframes typing': {
                    '0%, 60%, 100%': {
                      opacity: 0.3,
                      transform: 'translateY(0)',
                    },
                    '30%': {
                      opacity: 1,
                      transform: 'translateY(-4px)',
                    },
                  },
                }}
              >
                <Box className="dot" />
                <Box className="dot" />
                <Box className="dot" />
              </Box>
            </Box>
          </Box>
        )}
        {/* Scroll anchor for auto-scrolling */}
        <div ref={messagesEndRef} />
        {error && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2">
              {t('errors.generic')}:{' '}
              {error.message || t('errors.somethingWentWrong')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Feedback component */}
      {messages.length > 0 && !feedbackGiven && (
        <ChatFeedback onFeedback={handleChatFeedback} disabled={isLoading} />
      )}
    </Box>
  );
});

ChatInterface.displayName = 'ChatInterface';

// Main component - manages provider state
export default function ChatComponent() {
  const { t } = useTranslation('ai-assistant');
  const [provider, setProvider] = useState<AIProvider>('azure');
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef<ChatInterfaceHandle>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && chatRef.current && !isLoading) {
      chatRef.current.sendMessage(input);
      setInput('');
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Render ChatInterface with key to force remount on provider change */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', py: 2 }}>
        <ChatInterface
          key={provider}
          provider={provider}
          ref={chatRef}
          onLoadingChange={setIsLoading}
        />
      </Box>

      {/* Input Form - Fixed at bottom */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', gap: 1, px: 2, flexShrink: 0 }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('input.placeholder')}
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          slotProps={{
            input: {
              sx: { borderRadius: 3, bgcolor: 'background.paper' },
              endAdornment: (
                <>
                  {isLoading ? (
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        chatRef.current?.stop();
                      }}
                      size="small"
                      edge="end"
                      title={t('input.stop')}
                    >
                      <StopRoundedIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        if (input.trim() && chatRef.current && !isLoading) {
                          chatRef.current.sendMessage(input);
                          setInput('');
                        }
                      }}
                      disabled={isLoading || !input.trim()}
                      size="small"
                      color="primary"
                      edge="end"
                    >
                      <SendRoundedIcon />
                    </IconButton>
                  )}
                </>
              ),
            }
          }}
        />
      </Box>
      {/* Header - Provider selector and Clear button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        <Button
          onClick={() => chatRef.current?.clearMessages()}
          variant="text"
          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
        >
          {t('actions.clearChat')}
        </Button>
        <ProviderSelector value={provider} onChange={setProvider} />
      </Box>
    </Box>
  );
}
